import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, capitalizeFirstLetters, sortArrayOfObjects, loadSpecificData, getData } from '../../../js/Helpers';
import { saveDataLineEditor } from '../../../js/SaveFunctions';
import { replaceCourseIdInStudentRequests } from '../utils/courseRequestDraftHelpers';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import EditCourseData from '../modals/edit_course';
import { NO_DEPARTMENT_FILTER_VALUE, prependNoDepartmentFilterOption } from '../utils/departmentFilterUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload, faMinusSquare, faSortDown, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';

const normalizeDepartmentIDs = (department_ids = []) => {
	if(!Array.isArray(department_ids)) return [];

	return department_ids.reduce((results, department_id) => {
		const normalized_department_id = `${department_id || ''}`.trim();
		if(!normalized_department_id || normalized_department_id === '0' || normalized_department_id === 'null' || normalized_department_id === 'undefined') return results;
		if(results.includes(normalized_department_id)) return results;
		results.push(normalized_department_id);
		return results;
	}, []);
};

const normalizePriority = (priority) => {
	if(priority === null || priority === undefined || priority === '') return '50';
	const parsed_priority = Number(priority);
	return Number.isFinite(parsed_priority) ? `${parsed_priority}` : `${priority}`;
};

const buildCourseDraft = (course_info = {}) => ({
	course_id: course_info.course_id,
	course_code: course_info.course_code || '',
	name: course_info.name || '',
	priority: normalizePriority(course_info.priority),
	departments: normalizeDepartmentIDs(course_info.departments),
});

const mergeSavedCourseIntoList = (course_list = [], old_course_id, saved_course) => {
	const old_course_id_string = `${old_course_id}`;
	const new_course_id_string = `${saved_course.course_id}`;
	const original_course_index = course_list.findIndex((course_info) => `${course_info.course_id}` === old_course_id_string);

	if(old_course_id_string === new_course_id_string)
	{
		return course_list.map((course_info) => (
			`${course_info.course_id}` === old_course_id_string ? saved_course : course_info
		));
	}

	const next_course_list = course_list.filter((course_info) => (
		`${course_info.course_id}` !== old_course_id_string && `${course_info.course_id}` !== new_course_id_string
	));

	if(original_course_index === -1) return [...next_course_list, saved_course];

	const safe_insert_index = Math.min(original_course_index, next_course_list.length);
	return [
		...next_course_list.slice(0, safe_insert_index),
		saved_course,
		...next_course_list.slice(safe_insert_index),
	];
};

const coursesMatch = (course_a = {}, course_b = {}) => {
	const normalized_a = buildCourseDraft(course_a);
	const normalized_b = buildCourseDraft(course_b);

	return normalized_a.course_code.trim() === normalized_b.course_code.trim()
		&& normalized_a.name.trim() === normalized_b.name.trim()
		&& normalizePriority(normalized_a.priority) === normalizePriority(normalized_b.priority)
		&& JSON.stringify([...normalized_a.departments].sort()) === JSON.stringify([...normalized_b.departments].sort());
};

export default function DatabaseDataCourses({ schoolInfo, databaseData, setDatabaseData, setStudents, scheduleStructureData, scheduleInfo, teachers, students, classrooms, departments, sections, setSections = null, setSubsections = null, labels, dataIsLoading, handlePullNewData, readOnlyAddUpload = false, visibleDataFilter = null, departmentFilterOptions = departments, showDepartmentFilter = true}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);
	
	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [selectedData, setSelectedData] = useState([]);
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);

	const [sortedBy, setSortedBy] = useState('name');
	const [sortAscDesc, setSortAscDesc] = useState('asc');

	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const courseCodeInputRef = useRef(null);
	const courseNameInputRef = useRef(null);
	const priorityInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	const visibleDatabaseData = useMemo(() => (
		typeof visibleDataFilter === 'function' ? databaseData.filter(visibleDataFilter) : databaseData
	), [databaseData, visibleDataFilter]);
	const departmentFilterOptionsWithNoDepartment = useMemo(() => prependNoDepartmentFilterOption(departmentFilterOptions), [departmentFilterOptions]);
	const schoolDataFilterCategories = useMemo(() => {
		const categories = [];
		if(showDepartmentFilter)
		{
			categories.push({
				key: 'departments',
				title: 'Departments',
				dataKey: 'departments',
				noValueFilterValue: NO_DEPARTMENT_FILTER_VALUE,
				options: departmentFilterOptionsWithNoDepartment.map(department_info => ({
					value: department_info.department_id,
					display: department_info.department,
				})),
			});
		}
		categories.push({
			key: 'type',
			title: 'Type',
			options: [
				{value: '1', display: 'Elective'},
				{value: '0', display: 'Non-elective'},
			],
			matchRow: (course_info, selected_values) => {
				const course_type = `${course_info?.elective}` === '1' ? '1' : '0';
				return selected_values.some(selected_value => `${selected_value}` === course_type);
			},
		});
		return categories;
	}, [departmentFilterOptionsWithNoDepartment, showDepartmentFilter]);
	const getDepartmentSortText = useCallback((department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);
		if(normalized_department_ids.length === 0) return 'No department';

		return normalized_department_ids.map(department_id => {
			const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
			return department_index !== -1 ? departments[department_index].department : 'No department';
		}).sort((department_a, department_b) => `${department_a}`.localeCompare(`${department_b}`, undefined, {numeric:true, sensitivity:'base'})).join(', ');
	}, [departments]);
	const sortByDepartmentDisplay = useCallback((course_list = [], sort_asc_desc = 'asc') => (
		[...(Array.isArray(course_list) ? course_list : [])].sort((course_a, course_b) => {
			const comparison = getDepartmentSortText(course_a.departments).localeCompare(getDepartmentSortText(course_b.departments), undefined, {numeric:true, sensitivity:'base'});
			return sort_asc_desc === 'asc' ? comparison : comparison * -1;
		})
	), [getDepartmentSortText]);

	const applyCurrentSort = useCallback((course_list = []) => {
		if(sortedBy === 'departments') return sortByDepartmentDisplay(course_list, sortAscDesc);
		const sort_type = (sortedBy === 'priority') ? 'number' : null;
		return sortArrayOfObjects(Array.isArray(course_list) ? course_list : [], sortedBy, sort_type, sortAscDesc);
	}, [sortByDepartmentDisplay, sortedBy, sortAscDesc]);

	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};

	const isEditingField = useCallback((course_id, field) => (
		editingCell
		&& `${editingCell.course_id}` === `${course_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getCourseByID = useCallback((course_id) => (
		databaseData.find((course_info) => `${course_info.course_id}` === `${course_id}`) || null
	), [databaseData]);

	const startCellEdit = useCallback((course_info, field) => {
		if(!course_info) return;

		setEditingCell({course_id: course_info.course_id, field});
		setCellDraft(buildCourseDraft(course_info));
		setIsSavingCell(false);
	}, []);

	const getDepartmentName = (department_id) => {
		const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
		return department_index !== -1 ? departments[department_index].department : 'No department';
	};

	const renderDepartmentDisplay = (department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);

		if(normalized_department_ids.length === 0)
		{
			return <div className='school-data-department medium-text'>No department</div>;
		}

		return normalized_department_ids.map((department_id, index) => (
			<div className='school-data-department medium-text' key={`${department_id}-${index}`}>{getDepartmentName(department_id)}</div>
		));
	};

	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		
		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		let next_display_results = [];
		if(search_value.length > 1)
		{
			next_display_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['name', 'course_code']);
		}
		else
		{
			next_display_results = filteredResultsRef.current;
		}
		next_display_results = applyCurrentSort(next_display_results);
		setDisplayResults([...next_display_results]);
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [applyCurrentSort, dataIsLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			course_code: courseCodeInputRef,
			name: courseNameInputRef,
			priority: priorityInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const toggleDepartmentSelection = (department_id) => {
		const normalized_department_id = `${department_id}`;

		setCellDraft((current_draft) => {
			if(!current_draft) return current_draft;

			const department_is_selected = current_draft.departments.includes(normalized_department_id);
			const next_departments = department_is_selected
				? current_draft.departments.filter((selected_department_id) => `${selected_department_id}` !== normalized_department_id)
				: [...current_draft.departments, normalized_department_id];

			return {...current_draft, departments: next_departments};
		});
	};

	const handleSaveCell = useCallback(async (course_info) => {
		if(!cellDraft || !editingCell || isSavingCell) return;

		const trimmed_course_code = cellDraft.course_code ? cellDraft.course_code.trim() : '';
		const trimmed_name = cellDraft.name ? cellDraft.name.trim() : '';
		const priority = Number(cellDraft.priority);
		const normalized_departments = normalizeDepartmentIDs(cellDraft.departments);
		const draft_to_compare = {
			...course_info,
			course_code: trimmed_course_code,
			name: trimmed_name,
			priority: normalizePriority(cellDraft.priority),
			departments: normalized_departments,
		};

		if(coursesMatch(course_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_course_code || !trimmed_name)
		{
			swal({
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You are missing data! Please input all background info before saving.</p>
					</div>
				)
			});
			return;
		}

		if(!Number.isInteger(priority) || priority < 1 || priority > 100)
		{
			swal({
				title: "Invalid Priority",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You have an invalid priority number set. It should be an integer value between 1-100!</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);

		const original_course_id = course_info.course_id;
		const response_data = await saveDataLineEditor({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'course',
			data_to_save: {
				course_id: original_course_id,
				course_code: trimmed_course_code,
				name: trimmed_name,
				priority: `${priority}`,
				departments: normalized_departments,
			}
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: "Unable to Save",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>We couldn&rsquo;t save this course value. Please try again.</p>
					</div>
				)
			});
			return;
		}

		const saved_course_id = response_data.new_data_id;
		let saved_course = {
			...course_info,
			course_id: saved_course_id,
			course_code: trimmed_course_code,
			name: trimmed_name,
			priority: `${priority}`,
			departments: normalized_departments,
		};

		const refreshed_course = await loadSpecificData({
			data_type:'course',
			data_id:saved_course_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
		});

		if(refreshed_course && !refreshed_course.msg)
		{
			if(`${refreshed_course.course_code || ''}` !== trimmed_course_code)
			{
				setIsSavingCell(false);
				swal({
					title: "Unable to Verify Save",
					icon:"warning",
					buttons: {cancel: {text: "Okay", value: false, visible: true, className: 'blue-btn'}},
					content: (<div><p>The saved course code did not match the casing you entered. Please try again.</p></div>)
				});
				return;
			}
			saved_course = refreshed_course;
		}

		const next_students = replaceCourseIdInStudentRequests(students, original_course_id, saved_course_id);
		const next_sections = sections.map((section_info) => (
			`${section_info.course_id}` === `${original_course_id}`
				? {...section_info, course_id: saved_course_id, departments: saved_course.departments}
				: section_info
		));
		const next_database_data = mergeSavedCourseIntoList(databaseData, original_course_id, saved_course);
		const next_filtered_results = mergeSavedCourseIntoList(filteredResults, original_course_id, saved_course);

		setStudents(next_students);
		setSections(next_sections);
		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		setSelectedData((current_selected_data) => {
			if(`${saved_course_id}` === `${original_course_id}`) return current_selected_data;

			const next_selected_data = current_selected_data.map((selected_id) => (
				`${selected_id}` === `${original_course_id}` ? saved_course_id : selected_id
			));

			return [...new Set(next_selected_data)];
		});
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, students, sections, databaseData, filteredResults, setStudents, setSections, setDatabaseData, cancelCellEdit]);

	const handleCellBlur = (course_info, field) => {
		if(!isEditingField(course_info.course_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(course_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, course_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(course_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	useEffect(() => {
		if(!editingCell || editingCell.field !== 'departments') return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const course_info = getCourseByID(editingCell.course_id);
			if(!course_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(course_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getCourseByID, cancelCellEdit]);

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		cancelCellEdit();
		setSortedBy(sorted_by);
		setSortAscDesc(sort_asc_desc);
		if(sorted_by === 'departments')
		{
			setDisplayResults(sortByDepartmentDisplay(displayResults, sort_asc_desc));
			return;
		}
		setDisplayResults([...sortedData]);
	};
	
	const handleFilterClick = (selectedFilters = {}) => {
		cancelCellEdit();
		const filtered_data = applySchoolDataFilters(visibleDatabaseData, selectedFilters, schoolDataFilterCategories);
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		setFilteredResults([...filtered_data]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};

	const toggleSelectData = (data_id) => {
		const data_selected_index = selectedData.findIndex((data) => `${data}` === `${data_id}`);

		if(data_selected_index !== -1)
		{
			selectedData.splice(data_selected_index, 1);
		}
		else
		{
			selectedData.push(data_id);
		}

		setSelectedData([...selectedData]);
	};

	const toggleSelectAll = () => {
		const any_data_selected = selectedData.length > 0;
		
		let new_selected_data = [];

		if(!any_data_selected) 
		{
			new_selected_data = displayResults.reduce((results, course) => {
				results.push(course.course_id);
				return results;
			}, []);
		}

		setSelectedData(new_selected_data);
	};
	
	const handeDeleteData = async (data_ids) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'course', data_ids:data_ids};
		let delete_data_bool = true;
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: "Are you sure?",
				icon: "warning",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete the following courses:</p>
							<div style={{marginLeft:'50px', textAlign:'left'}}>
								{data_ids.map((data_id, i) => {
									const data_index = databaseData.findIndex((course) => `${course.course_id}` === `${data_id}`);
									const data_info = databaseData[data_index];

									return (
										<div key={i}>{data_info.name} ({data_info.course_code})</div>
									);
								})}
							</div>
							<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			try
			{
				const response_data = await deleteData(data, {throwOnError:true});
				if(response_data === null) throw new Error('Unable to delete course data.');

				const new_database_data = databaseData.filter((data_info) => !data_ids.some((data_id) => `${data_id}` === `${data_info.course_id}`));
				const new_filtered_data = filteredResults.filter((filtered_data) => !data_ids.some((data_id) => `${data_id}` === `${filtered_data.course_id}`));

				setDatabaseData(new_database_data);
				setFilteredResults(new_filtered_data);
				setSelectedData([]);

				const section_data = await getData('sections', '/get-sections', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true);
				if(section_data)
				{
					if(typeof setSections === 'function') setSections(Array.isArray(section_data.sections) ? [...section_data.sections] : []);
					if(typeof setSubsections === 'function') setSubsections(Array.isArray(section_data.subsections) ? [...section_data.subsections] : []);
				}
			}
			catch(error)
			{
				console.log(error?.response || error);
				swal({
					title: "Unable to Delete",
					icon:"warning",
					buttons: {
						cancel: {
							text: "Okay",
							value: false,
							visible: true,
							className: 'blue-btn'
						}
					},
					content: (
						<div>
							<p>We couldn&rsquo;t delete this course. Please try again.</p>
						</div>
					)
				});
			}
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);
		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 0) handleChange();
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(dataIsLoading || filterActive) return;

		filteredResultsRef.current = Array.isArray(visibleDatabaseData) ? visibleDatabaseData : [];
		setFilteredResults([...filteredResultsRef.current]);
		setHandleChangeComplete(false);
	}, [visibleDatabaseData, dataIsLoading, filterActive]);
	
	const row_style = {gridTemplateColumns:'35px minmax(110px, 1fr) minmax(220px, 3fr) minmax(180px, 2fr) 90px 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditCourseData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} courses={databaseData} students={students} classrooms={classrooms} teachers={teachers} departments={departments} setDatabaseData={setDatabaseData} setStudents={setStudents} sections={sections} setSections={setSections} labels={labels} filteredResults={filteredResults} setFilteredResults={setFilteredResults}/>
		}
		{showUploadModal && !readOnlyAddUpload &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Courses' uploadAPIName='coursedata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Courses</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder="Search for courses..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
				</div>
				{!readOnlyAddUpload && <div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>}
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div></div>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='course_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Course Name <SortToggle handleOnClick={handleSortClick} sortKey='name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Department(s) <SortToggle handleOnClick={handleSortClick} sortKey='departments' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Priority <SortToggle handleOnClick={handleSortClick} sortKey='priority' passedData={displayResults}/></div>
					{!readOnlyAddUpload ? <div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div> : <div></div>}
				</div>
				{displayResults.length > 0 &&
					<div className='select-all-row'>
						<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						{selectedData.length > 0 &&
							<div className='small-text'>
								<div className='tooltip' data-tooltip-text='Delete courses' onClick={() => handeDeleteData(selectedData)}>
									<div className='red-link'>{`Delete ${selectedData.length} selected`}</div>
								</div>
							</div>
						}
					</div>
				}
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{Object.keys(scheduleStructureData).length > 0 &&
						<>
						{displayResults.map((data_info) => {
							if(!data_info.name) return null;

							const course_id = data_info.course_id;
							const data_selected = selectedData.some((selected_id) => `${selected_id}` === `${course_id}`);
							const editing_course_id = editingCell?.course_id;
							
							return (
								<div className='school-data-database-row' style={row_style} key={data_info.course_id}>
									<div className='school-data-row-checkbox-cell' onClick={() => toggleSelectData(course_id)}>
										<FontAwesomeIcon className={data_selected ? 'blue-text' : 'dark-gray-text'} icon={data_selected ? faCheckSquare : farSquare}/>
									</div>
									<div
										ref={isEditingField(course_id, 'course_code') ? activeEditorRef : null}
										className={isEditingField(course_id, 'course_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_course_id ? () => startCellEdit(data_info, 'course_code') : undefined}
									>
										{isEditingField(course_id, 'course_code') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={courseCodeInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.course_code || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('course_code', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'course_code')}
												/>
											)
										:
											<div>{data_info.course_code}</div>
										}
									</div>
									<div
										ref={isEditingField(course_id, 'name') ? activeEditorRef : null}
										className={isEditingField(course_id, 'name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_course_id ? () => startCellEdit(data_info, 'name') : undefined}
									>
										{isEditingField(course_id, 'name') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={courseNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.name || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('name', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'name')}
												/>
											)
										:
											<div>{capitalizeFirstLetters(data_info.name)}</div>
										}
									</div>
									<div
										ref={isEditingField(course_id, 'departments') ? activeEditorRef : null}
										className={isEditingField(course_id, 'departments') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_course_id ? () => startCellEdit(data_info, 'departments') : undefined}
									>
										{isEditingField(course_id, 'departments') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<div className='school-data-inline-multiselect-popover'>
													{departments.length === 0 ?
														<div className='medium-text'>No departments available.</div>
													:
														departments.map((department_info) => {
															const department_id = `${department_info.department_id}`;
															const department_selected = cellDraft?.departments?.includes(department_id);

															return (
																<label key={department_id} className='school-data-inline-multiselect-option'>
																	<input
																		type='checkbox'
																		checked={department_selected}
																		disabled={isSavingCell}
																		onChange={() => toggleDepartmentSelection(department_id)}
																	/>
																	<span>{department_info.department}</span>
																</label>
															);
														})
													}
												</div>
											)
										:
											<div>{renderDepartmentDisplay(data_info.departments)}</div>
										}
									</div>
									<div
										ref={isEditingField(course_id, 'priority') ? activeEditorRef : null}
										className={isEditingField(course_id, 'priority') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_course_id ? () => startCellEdit(data_info, 'priority') : undefined}
									>
										{isEditingField(course_id, 'priority') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={priorityInputRef}
													className='school-data-inline-edit-input'
													type='number'
													min='1'
													max='100'
													value={cellDraft?.priority || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('priority', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'priority')}
												/>
											)
										:
											<div>{data_info.priority}</div>
										}
									</div>
									<div className='school-data-database-row-btn-container'>
										<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(course_id)}/>
										<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData([course_id])}/>
									</div>
								</div>
							);
						})}
						</>
					}
					</>
				)}
			</div>
		</div>
		</>
	);
}
